import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { groupService } from "../services/group.service";

export const fetchGroups = createAsyncThunk("group/fetchAll", async (_, thunkAPI) => {
  try {
    return await groupService.getGroups();
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to fetch groups");
  }
});

export const fetchActiveGroups = createAsyncThunk("group/fetchActive", async (_, thunkAPI) => {
  try {
    return await groupService.getActiveGroups();
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to fetch active groups");
  }
});

export const fetchGroupById = createAsyncThunk("group/fetchById", async (id, thunkAPI) => {
  try {
    return await groupService.getGroupById(id);
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to fetch group");
  }
});

export const addGroup = createAsyncThunk("group/add", async (group, thunkAPI) => {
  try {
    return await groupService.createGroup(group);
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to create group");
  }
});

export const editGroup = createAsyncThunk("group/edit", async ({ id, group }, thunkAPI) => {
  try {
    await groupService.updateGroup(id, group);
    return { id, ...group };
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to update group");
  }
});

export const removeGroup = createAsyncThunk("group/remove", async (id, thunkAPI) => {
  try {
    await groupService.deleteGroup(id);
    return id;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to delete group");
  }
});

// --- IMPORT GROUPS THUNK ---
export const importGroups = createAsyncThunk(
  "group/import",
  async (file, { rejectWithValue }) => {
    try {
      const result = await groupService.importGroups(file);
     
      return result;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to import groups");
    }
  }
);

const initialState = {
  groups: [],
  totalCount: 0,
  loading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
};

const groupSlice = createSlice({
  name: "group",
  initialState,
  reducers: {
    openModal: (state) => { state.showModal = true; },
    closeModal: (state) => { state.showModal = false; },
    openDeleteModal: (state) => { state.showDeleteModal = true; },
    closeDeleteModal: (state) => { state.showDeleteModal = false; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      // Fetch groups
      .addCase(fetchGroups.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
        state.totalCount = action.payload?.totalCount || state.groups.length;
      })
      .addCase(fetchGroups.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      
      // Fetch active groups
      .addCase(fetchActiveGroups.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchActiveGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = Array.isArray(action.payload) ? action.payload : (action.payload?.items || []);
      })
      .addCase(fetchActiveGroups.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      
      // Add group
      .addCase(addGroup.pending, (state) => { state.loading = true; })
      .addCase(addGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.groups.push(action.payload);
        state.showModal = false;
      })
      .addCase(addGroup.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      
      // Edit group
      .addCase(editGroup.pending, (state) => { state.loading = true; })
      .addCase(editGroup.fulfilled, (state, action) => {
        state.loading = false;
        const { id, ...updatedGroup } = action.payload; // Adjust based on what your API returns
        const index = state.groups.findIndex((g) => g.id === id || g.groupId === id);
        if (index !== -1) {
          state.groups[index] = { ...state.groups[index], ...updatedGroup };
        }
        state.showModal = false;
      })
      .addCase(editGroup.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      
      // Remove group
      .addCase(removeGroup.pending, (state) => { state.loading = true; })
      .addCase(removeGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = state.groups.filter((group) => group.id !== action.payload && group.groupId !== action.payload);
        state.showDeleteModal = false;
      })
      .addCase(removeGroup.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // --- IMPORT GROUPS REDUCERS ---
      .addCase(importGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importGroups.fulfilled, (state) => {
        state.loading = false;
        // List will be refreshed by the component or uncomment dispatch(fetchGroups()) in the thunk
      })
      .addCase(importGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { openModal, closeModal, openDeleteModal, closeDeleteModal, clearError } = groupSlice.actions;
export default groupSlice.reducer;